import { useState, useCallback } from 'react';
import { lsGet, lsSet } from '../lib/storage';

const RANK = { unvisited: 0, read: 1, study: 2, ai: 3 };
const key = poemId => `diwanVD-${poemId}`;

/**
 * Tracks per-verse study depth for a poem, persisted in localStorage.
 *
 * Depth levels (ascending):
 *   unvisited  → navigated to the verse (read)
 *   read       → opened Study tab on this verse (study)
 *   study      → opened Ask AI on this verse (ai)
 *   ai         → manually marked via the strip button
 *
 * Returns { depths, markRead, markStudy, markAI, markManual }
 * where depths is { [verseIdx]: 'read' | 'study' | 'ai' }
 */
export function useVerseDepth(poemId) {
  const [depths, setDepths] = useState(() => {
    try { return JSON.parse(lsGet(key(poemId)) ?? '{}'); } catch { return {}; }
  });

  const upgrade = useCallback((idx, level) => {
    setDepths(prev => {
      const cur = RANK[prev[idx]] ?? 0;
      if (RANK[level] <= cur) return prev;
      const next = { ...prev, [idx]: level };
      lsSet(key(poemId), JSON.stringify(next));
      return next;
    });
  }, [poemId]);

  const markRead   = useCallback(idx => upgrade(idx, 'read'),   [upgrade]);
  const markStudy  = useCallback(idx => upgrade(idx, 'study'),  [upgrade]);
  const markAI     = useCallback(idx => upgrade(idx, 'ai'),     [upgrade]);
  const markManual = useCallback(idx => upgrade(idx, 'study'),  [upgrade]);

  // Undo a manual 'study' mark — drops back to 'read'. Only acts on 'study' depth
  // (not 'ai', which was earned by actually using Ask AI).
  const unmarkStudy = useCallback((idx) => {
    setDepths(prev => {
      if (prev[idx] !== 'study') return prev;
      const next = { ...prev, [idx]: 'read' };
      lsSet(key(poemId), JSON.stringify(next));
      return next;
    });
  }, [poemId]);

  // Retroactively populate depths from an existing PoemSession.
  // Called once when a session is loaded that predates the depth tracking system.
  // verseId format: "${poemId}_v${index}" — we extract the numeric index.
  const backfillFromSession = useCallback((session) => {
    if (!session?.verseHistory?.length) return;
    session.verseHistory.forEach(entry => {
      const m = entry.verseId?.match(/_v(\d+)$/);
      if (m) upgrade(parseInt(m[1], 10), 'ai');
    });
  }, [upgrade]);

  return { depths, markRead, markStudy, markAI, markManual, unmarkStudy, backfillFromSession };
}
